"use client";

import { useMemo, useState } from "react";
import type { CalendarEkadashiItem } from "@/lib/api/calendar";

export interface EkadashiYearData {
  year: number;
  ekadashis: CalendarEkadashiItem[];
}

function formatLongDate(iso: string): string {
  // iso is the engine-computed date string (YYYY-MM-DD). Never typed by hand.
  const parsed = new Date(`${iso}T00:00:00+05:30`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
}

function todayISO(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const v = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${v.year}-${v.month}-${v.day}`;
}

/**
 * Engine-backed Ekadashi date table (rule 23/24). Every date in this table is
 * the value the backend astrology engine returned for that Ekadashi vrat —
 * NO date is hand-typed in this component or anywhere on the page. The "next
 * Ekadashi" highlight is derived by comparing engine dates to today's IST date,
 * never by a literal.
 */
export default function EkadashiDates({ years }: { years: EkadashiYearData[] }) {
  const available = years.filter((y) => y.ekadashis.length > 0);
  const [selected, setSelected] = useState<number>(available[0]?.year ?? years[0]?.year ?? 2026);

  const today = todayISO();
  const active = useMemo(
    () => years.find((y) => y.year === selected) ?? years[0],
    [years, selected]
  );

  const nextDate = useMemo(() => {
    const allDates = years
      .flatMap((y) => y.ekadashis.map((e) => e.date))
      .filter((d) => d >= today)
      .sort();
    return allDates[0] ?? null;
  }, [years, today]);

  if (available.length === 0) {
    return (
      <p className="text-[15px] leading-[1.75]" style={{ color: "var(--ish-text-muted)" }}>
        Ekadashi dates are computed live from the Vedic engine. The schedule could not be loaded
        right now — please refresh in a moment to see every Ekadashi vrat date with its tithi.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label="Ekadashi year">
        {years.map((y) => {
          const isActive = y.year === selected;
          const isEmpty = y.ekadashis.length === 0;
          return (
            <button
              key={y.year}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={isEmpty}
              onClick={() => setSelected(y.year)}
              className="px-4 py-1.5 rounded-lg text-[13px] font-semibold transition disabled:opacity-40"
              style={{
                backgroundColor: isActive ? "var(--ish-accent)" : "var(--ish-accent-light)",
                color: isActive ? "#fff" : "var(--ish-accent)",
                border: "1px solid var(--ish-accent-border)",
              }}
            >
              Ekadashi {y.year}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid var(--ish-border)" }}>
        <table className="w-full border-collapse text-left text-[13px]">
          <thead>
            <tr style={{ backgroundColor: "var(--ish-accent-light)" }}>
              <th className="px-3 py-2.5 font-semibold" style={{ color: "var(--ish-dark)" }}>
                Ekadashi
              </th>
              <th className="px-3 py-2.5 font-semibold" style={{ color: "var(--ish-dark)" }}>
                Date
              </th>
              <th className="px-3 py-2.5 font-semibold" style={{ color: "var(--ish-dark)" }}>
                Hindu month
              </th>
            </tr>
          </thead>
          <tbody>
            {active.ekadashis.map((e) => {
              const isNext = e.date === nextDate;
              return (
                <tr
                  key={e.id}
                  style={{
                    borderTop: "1px solid var(--ish-border-light)",
                    backgroundColor: isNext ? "var(--ish-accent-light)" : undefined,
                  }}
                >
                  <td className="px-3 py-2.5">
                    <span className="font-semibold" style={{ color: "var(--ish-dark)" }}>
                      {e.name_en}
                    </span>
                    {e.name_hi ? (
                      <span className="ml-1.5" style={{ color: "var(--ish-text-muted)" }}>
                        ({e.name_hi})
                      </span>
                    ) : null}
                    {isNext ? (
                      <span
                        className="ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                        style={{ backgroundColor: "var(--ish-accent)", color: "#fff" }}
                      >
                        Next
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "var(--ish-text)" }}>
                    {formatLongDate(e.date)}
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "var(--ish-text-muted)" }}>
                    {e.month_hi || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
        All Ekadashi dates are computed from real planetary positions using the Swiss Ephemeris
        engine with Lahiri Ayanamsa — the tithi boundary, not a fixed calendar.
      </p>
    </div>
  );
}
